import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MdAddBox, MdRssFeed, MdCheckCircle, MdUpload, MdFileDownload, MdMyLocation, MdPhotoCamera } from 'react-icons/md';
import api from '../api/axios';
import IdentifierBadges from '../components/IdentifierBadges';
import { downloadFromApi } from '../lib/fileDownload';
import { tsFilename } from '../lib/filename';
import SearchableSelect from '../components/SearchableSelect';
import ImportProgress from '../components/ImportProgress';
import ImportErrorReport from '../components/ImportErrorReport';
import { useAssetImport, importErrorMessage } from '../lib/useAssetImport';

const Toast = ({ msg, type, onClose }) => (
  <div className={`toast toast-${type}`} style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
    {msg || '(no message — see browser console)'}
    <button onClick={onClose} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 16 }}>×</button>
  </div>
);

const randRfid = () =>
  `E200-${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}-${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}`;

const AssetRegistration = () => {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [masters, setMasters] = useState({});       // key -> {allow_user_add, items:[value]}
  const [generalNames, setGeneralNames] = useState([]); // [{name,category}]
  const [locations, setLocations] = useState([]);
  const [statuses, setStatuses] = useState(['active']);
  const [values, setValues] = useState({});
  const [photoFiles, setPhotoFiles] = useState({}); // field_key -> File
  const [gps, setGps] = useState(null);             // {lat,lng}
  const [recentAssets, setRecentAssets] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const importRef = useRef();
  // Excel-import progress + all-or-nothing failure report. Same shared hook
  // AllAssets uses — the upload plumbing lives in lib/useAssetImport.js.
  const {
    phase: importPhase,
    percent: importPercent,
    fileName: importFileName,
    importing,
    report: importErrors,
    clearReport: clearImportErrors,
    runImport: uploadImport,
  } = useAssetImport();

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };
  const template = templates.find(t => t.id === templateId) || null;

  const loadMasters = useCallback(() => {
    api.get('/masters').then(r => {
      const map = {};
      (r.data || []).forEach(m => { map[m.key] = { allow_user_add: m.allow_user_add, items: m.items.filter(i => i.active).map(i => i.value) }; });
      setMasters(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/templates').then(r => {
      const list = (r.data || []).filter(t => t.active);
      setTemplates(list);
      if (list.length) setTemplateId(list[0].id);
    }).catch(() => {});
    loadMasters();
    api.get('/general-names').then(r => setGeneralNames(r.data || [])).catch(() => {});
    api.get('/locations').then(r => setLocations(r.data)).catch(() => {});
    api.get('/assets/statuses').then(r => setStatuses(r.data.statuses)).catch(() => {});
    fetchRecent();
  }, [loadMasters]);

  const fetchRecent = async () => {
    // Ten rows, asked for as ten. This used to download the entire asset
    // table and throw all but the first ten away.
    try {
      const res = await api.get('/assets', { params: { limit: 10 } });
      setRecentAssets(res.data || []);
    } catch (err) {
      console.warn('[register] recent list failed', err);
      setRecentAssets([]);
    }
  };

  // Reset the value map when the template changes.
  useEffect(() => {
    if (!template) return;
    const init = {};
    template.fields.forEach(f => { if (f.field_type === 'status') init[f.field_key] = 'active'; });
    setValues(init); setPhotoFiles({}); setGps(null); setErrors({});
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setVal = (key, value) => {
    setValues(v => ({ ...v, [key]: value }));
    if (errors[key]) setErrors(e => ({ ...e, [key]: '' }));
  };

  // General Name → auto-fill the category_auto field.
  const onGeneralName = (fieldKey, name) => {
    setVal(fieldKey, name);
    const gn = generalNames.find(g => g.name === name);
    const catField = template.fields.find(f => f.field_type === 'category_auto');
    if (catField) setVal(catField.field_key, gn ? gn.category : '');
  };

  const captureLocation = () => {
    if (!navigator.geolocation) { showToast('Geolocation not supported by this browser', 'error'); return; }
    navigator.geolocation.getCurrentPosition(
      pos => { setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }); showToast('Location captured', 'success'); },
      () => showToast('Could not get location (permission denied?)', 'error'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const validate = () => {
    const errs = {};
    (template?.fields || []).forEach(f => {
      if (!f.required) return;
      // photo / GPS values live outside `values` (photoFiles / gps), so their
      // required flag must be checked against that state, not the value map.
      if (f.field_type === 'photo') { if (!photoFiles[f.field_key]) errs[f.field_key] = `${f.label} is required`; return; }
      if (f.field_type === 'auto_gps') { if (!gps) errs[f.field_key] = `${f.label} is required`; return; }
      if (['auto_assetid', 'auto_timestamp', 'category_auto'].includes(f.field_type)) return;
      const v = values[f.field_key];
      if (v === undefined || v === null || String(v).trim() === '') errs[f.field_key] = `${f.label} is required`;
    });
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!template) return;
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); showToast('Please fill the required fields', 'error'); return; }
    setLoading(true);
    const failedMasters = [];
    try {
      // Persist any new master values the user typed.
      for (const f of template.fields) {
        if (f.field_type === 'master_dropdown' && f.master_key) {
          const val = values[f.field_key];
          const m = masters[f.master_key];
          if (val && m?.allow_user_add && !m.items.includes(val)) {
            try { await api.post(`/masters/${f.master_key}/items`, { value: val }); }
            catch (e2) { console.warn(`[register] failed to persist new "${f.label}" value "${val}":`, e2); failedMasters.push(f.label); }
          }
        }
      }
      const body = { template_id: template.id, values };
      if (gps) { body.latitude = gps.lat; body.longitude = gps.lng; }
      const res = await api.post('/assets', body);
      const assetId = res.data.id;
      // Upload photos (each with its label). Collect any that fail so we can
      // warn instead of falsely reporting a clean success.
      const failedPhotos = [];
      for (const f of template.fields) {
        if (f.field_type === 'photo' && photoFiles[f.field_key]) {
          const label = (f.config && f.config.label) || f.label;
          const fd = new FormData();
          fd.append('image', photoFiles[f.field_key]);
          fd.append('label', label);
          try { await api.post(`/assets/${assetId}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); }
          catch (e3) { console.warn(`[register] photo "${label}" upload failed:`, e3); failedPhotos.push(label); }
        }
      }
      const code = res.data.asset_code ? ` — ${res.data.asset_code}` : '';
      if (failedPhotos.length || failedMasters.length) {
        const notes = [];
        if (failedPhotos.length) notes.push(`${failedPhotos.length} photo(s) failed to upload`);
        if (failedMasters.length) notes.push(`${failedMasters.length} new list value(s) not saved for reuse`);
        showToast(`Asset saved${code}, but ${notes.join(' and ')}.`, 'warning');
      } else {
        showToast(`Asset registered${code}!`, 'success');
      }
      const keep = {}; template.fields.forEach(f => { if (f.field_type === 'status') keep[f.field_key] = 'active'; });
      setValues(keep); setPhotoFiles({}); setGps(null); setErrors({});
      loadMasters();
      fetchRecent();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to register asset', 'error');
    } finally { setLoading(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    // Reset up front so re-picking the SAME file after fixing it still fires
    // a change event.
    e.target.value = '';
    if (!file || importing) return;

    const url = templateId ? `/assets/import?template_id=${templateId}` : '/assets/import';
    // Upload + progress live in the shared hook; it reports an outcome rather
    // than throwing. See lib/useAssetImport.js.
    const outcome = await uploadImport(file, { url });
    if (outcome.status === 'busy') return;

    // 422 = all-or-nothing validation failure. NOTHING was imported; the
    // <ImportErrorReport> panel below carries the row-by-row detail and the
    // downloadable workbook.
    if (outcome.status === 'invalid') {
      const { failed = 0, total = 0 } = outcome.report || {};
      showToast(`Import cancelled — ${failed} of ${total} row(s) have errors. Nothing was imported.`, 'error');
      return;
    }

    if (outcome.status === 'error') {
      showToast(`Import failed — ${importErrorMessage(outcome.error)}`, 'error');
      return;
    }

    const {
      totalRows = 0, inserted = 0, updated = 0, errorCount = 0, errors = [],
      templateUsed, templateAutoPickedFrom,
    } = outcome.data || {};
    const parts = []; if (inserted) parts.push(`${inserted} added`); if (updated) parts.push(`${updated} updated`);
    if (errors.length) console.log('[import] errors:', errors.slice(0, 5));
    const summary = parts.length ? parts.join(' · ') : 'no changes';
    // The server imports against the template the FILE belongs to when the one
    // selected here matches none of its columns. Say so, or the operator sees
    // rows appear under a template they didn't pick and assumes it went wrong.
    const usedNote = templateAutoPickedFrom && templateUsed
      ? ` · matched the "${templateUsed}" template (not "${templateAutoPickedFrom}")`
      : '';
    showToast(totalRows === 0 ? 'No rows imported — the sheet has a header but no data rows'
      : `Import complete · ${summary}${usedNote}${errorCount ? ` · ${errorCount} skipped (see console)` : ''}`,
      errorCount && !inserted && !updated ? 'error' : 'success');
    fetchRecent();
  };

  const handleExportTemplate = async () => {
    try {
      const url = templateId ? `/assets/import-template?template_id=${templateId}` : '/assets/import-template';
      await downloadFromApi(url, tsFilename(`${(template?.name || 'asset').replace(/\W+/g, '-').toLowerCase()}-template`, 'xlsx'));
    } catch (err) { showToast(err.message || 'Template download failed', 'error'); }
  };

  const errStyle = (key) => ({ border: errors[key] ? '1.5px solid var(--danger)' : undefined });
  const errMsg = (key) => errors[key] && <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 4 }}>{errors[key]}</div>;

  // ── Render one dynamic field ────────────────────────────────────────────────
  const renderField = (f) => {
    const v = values[f.field_key] ?? '';
    const common = { className: 'form-control', value: v, style: errStyle(f.field_key), onChange: e => setVal(f.field_key, e.target.value) };
    switch (f.field_type) {
      case 'textarea':
        return <textarea {...common} rows={2} placeholder={f.label} />;
      case 'number':
        return <input {...common} type="number" placeholder={f.label} />;
      case 'date':
        return <input {...common} type="date" />;
      case 'status':
        return <SearchableSelect {...common}>{statuses.map(s => <option key={s} value={s}>{s}</option>)}</SearchableSelect>;
      case 'location':
        return <SearchableSelect {...common}><option value="">Select location</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.location_code} - {l.location_name}</option>)}</SearchableSelect>;
      case 'dropdown':
        return <SearchableSelect {...common}><option value="">Select</option>
          {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}</SearchableSelect>;
      case 'general_name':
        return <><SearchableSelect {...common} onChange={e => onGeneralName(f.field_key, e.target.value)}>
          <option value="">Select general name</option>
          {generalNames.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
        </SearchableSelect>{generalNames.length === 0 && <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>The platform owner can add entries under Masters → General Names.</div>}</>;
      case 'category_auto':
        return <input className="form-control" value={v} readOnly placeholder="Auto from General Name" style={{ background: '#f6f2f6' }} />;
      case 'master_dropdown': {
        const m = masters[f.master_key] || { allow_user_add: 0, items: [] };
        if (m.allow_user_add) {
          // Searchable like every other dropdown, but this master accepts new
          // values — type something unlisted and pick "Use ..." to keep it.
          return <SearchableSelect {...common} allowCustom placeholder="Select or type…">
            {m.items.map(it => <option key={it} value={it}>{it}</option>)}
          </SearchableSelect>;
        }
        return <SearchableSelect {...common}><option value="">Select</option>{m.items.map(it => <option key={it} value={it}>{it}</option>)}</SearchableSelect>;
      }
      case 'rfid':
        return <div style={{ display: 'flex', gap: 8 }}>
          <input {...common} style={{ ...errStyle(f.field_key), flex: 1 }} placeholder="e.g. E200-0001-A001" />
          <button type="button" className="btn btn-accent btn-sm" style={{ whiteSpace: 'nowrap' }}
            onClick={() => setVal(f.field_key, randRfid())}><MdRssFeed /> Simulate</button>
        </div>;
      case 'photo':
        return <div>
          <input type="file" accept="image/*" onChange={e => setPhotoFiles(p => ({ ...p, [f.field_key]: e.target.files[0] || null }))} />
          {photoFiles[f.field_key] && <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 4 }}><MdPhotoCamera style={{ verticalAlign: -2 }} /> {photoFiles[f.field_key].name}</div>}
        </div>;
      case 'auto_timestamp':
        return <input className="form-control" value="Captured automatically on save" readOnly style={{ background: '#f6f2f6' }} />;
      case 'auto_gps':
        return <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input className="form-control" value={gps ? `${gps.lat.toFixed(6)}, ${gps.lng.toFixed(6)}` : 'Not captured'} readOnly style={{ background: '#f6f2f6', flex: 1 }} />
          <button type="button" className="btn btn-outline btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={captureLocation}><MdMyLocation /> Capture</button>
        </div>;
      case 'auto_assetid':
        return <input className="form-control" value={`Auto-generated (${template?.code_prefix || 'AST'}-…)`} readOnly style={{ background: '#f6f2f6' }} />;
      case 'image_url':
        // A link to a picture hosted elsewhere — stored as the asset's image,
        // so the asset shows it without the file ever being uploaded here.
        return <input {...common} type="url" placeholder="https://… link to the asset's picture" />;
      default: // text, serial, qr, barcode
        return <input {...common} placeholder={f.label} />;
    }
  };

  return (
    <div className="page-container">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div className="page-header">
        <div>
          <div className="page-title"><MdAddBox /> Asset Registration</div>
          <div className="page-subtitle">Register assets using a template</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-outline btn-sm" onClick={handleExportTemplate}><MdFileDownload /> Template</button>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => importRef.current.click()}
            disabled={importing}
            title={importing ? 'An import is already running' : 'Import assets from an Excel file'}
          >
            <MdUpload /> {importing ? 'Importing…' : 'Import Excel'}
          </button>
          <input ref={importRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} disabled={importing} onChange={handleImport} />
        </div>
      </div>

      {/* Live upload → processing bar for the Excel import (shared component). */}
      <ImportProgress phase={importPhase} percent={importPercent} fileName={importFileName} />

      {/* All-or-nothing validation failure (HTTP 422): nothing was imported. */}
      <ImportErrorReport report={importErrors} onClose={clearImportErrors} onToast={showToast} />

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div className="card-title"><MdRssFeed style={{ color: 'var(--accent)' }} /> New Asset Details</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>Template</span>
            <SearchableSelect className="form-control" style={{ width: 'auto', minWidth: 200 }}
              value={templateId || ''} onChange={e => setTemplateId(Number(e.target.value))}>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </SearchableSelect>
          </div>
        </div>

        {!template ? <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading templates…</div> : (
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {template.fields.map(f => (
                <div className="form-group" key={f.id || f.field_key}>
                  <label className="form-label">{f.label}{f.required ? ' *' : ''}</label>
                  {renderField(f)}
                  {errMsg(f.field_key)}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
                {loading ? 'Saving...' : <><MdCheckCircle /> Register Asset</>}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => { const keep = {}; template.fields.forEach(f => { if (f.field_type === 'status') keep[f.field_key] = 'active'; }); setValues(keep); setPhotoFiles({}); setGps(null); setErrors({}); }}>
                Clear Form
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Recent assets */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Recently Registered Assets</div>
          <span className="badge badge-info">{recentAssets.length} records</span>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Asset ID</th><th>Asset Name</th><th>Category</th><th>RFID</th>
                <th>Identifiers</th><th>Location</th><th>Status</th><th>Registered</th>
              </tr>
            </thead>
            <tbody>
              {recentAssets.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No assets registered yet</td></tr>
              ) : recentAssets.map(asset => (
                <tr key={asset.id}>
                  <td><code style={{ fontSize: 11, color: 'var(--accent)' }}>{asset.asset_code || asset.asset_tag || '—'}</code></td>
                  <td style={{ fontWeight: 500 }}>{asset.asset_name}</td>
                  <td>{asset.category || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td><code style={{ fontSize: 11 }}>{asset.rfid_tag || '—'}</code></td>
                  <td><IdentifierBadges asset={asset} /></td>
                  <td>{asset.location_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td><span className={`badge status-${asset.status}`}>{asset.status}</span></td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{new Date(asset.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AssetRegistration;
